/**
 * What the paperclip in Chat accepts, and what each file becomes before a change is sent.
 *
 * The owner's ask (2026-09-29): "In the chat I should be able to add documents — say I want to
 * add a couple of images to the site as carousel backgrounds." Photos and plain text for now;
 * PDF and Word later.
 *
 * Every photo is re-encoded as a JPEG no wider than 2000px, unconditionally, for the phone's
 * reason (src/onboarding/pickPhotos.ts, gaps 9 and 10): an iPhone hands over HEIC, which Safari
 * renders and nothing else does, so a photo sent as it came is a broken image for most of the
 * people a Saddle link is shared with — and a 12-megapixel original is a slow page for nothing.
 * The phone's module is not imported: it reaches expo-image-manipulator, which the web's
 * boundary test keeps out of the browser. The naming rule is copied from it, so the two clients
 * name the same photo the same way.
 *
 * A photo the browser cannot decode — HEIC in Chrome, typically — is left out and said by name,
 * never attached as it came: the one that will not convert is the likeliest to be the one no
 * other browser can show.
 */

import { request } from '../../api/client';

export const MAX_PHOTOS = 10;
/**
 * The most photos a NEW app is made with: the phone's number (MAX_PHOTOS in
 * src/onboarding/pickPhotos.ts), so the demo's five or six arrive in one pick, not six.
 */
export const MAX_CREATE_PHOTOS = 12;
/**
 * The most photos a site keeps: the server stores `assets.slice(0, 50)`, and new ones go on the
 * end — so past it a photo the edit was told about is silently not kept.
 */
export const MAX_SITE_PHOTOS = 50;
export const MAX_PHOTO_WIDTH = 2000;
export const JPEG_QUALITY = 0.85;
/** The server's limits for text sent with a change (the shared POST /edit contract). */
export const MAX_TEXT_FILES = 3;
export const MAX_TEXT_CHARS = 20_000;
export const MAX_TEXT_TOTAL = 40_000;
/** The server keeps names of 1–120 characters. */
const MAX_NAME = 120;

/** For the file picker: the formats phones and cameras hand over, and plain text. */
export const ACCEPT = [
  'image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif', '.heic', '.heif',
  '.txt', '.md', 'text/plain', 'text/markdown',
].join(',');

export type Picked =
  | {
    kind: 'photo';
    /** This pick, and no other: what a resend of the same change is recognised by. */
    key: string;
    /** What the page will call it — `S.media(name)`. */
    name: string;
    /** The file's own name, for anything said about it. */
    original: string;
    /** The re-encoded JPEG, under `name`. */
    file: File;
  }
  | { kind: 'text'; key: string; name: string; size: number; text: string };

export interface PickDeps {
  /** Re-encode as a JPEG no wider than MAX_PHOTO_WIDTH. Throws when the file cannot be decoded. */
  toJpeg?: (file: File) => Promise<Blob>;
}

let picks = 0;
const newKey = () => `pick-${Date.now().toString(36)}-${(picks += 1).toString(36)}`;

/** A photo, by type or — where the browser gives none, as for HEIC in Chrome — by name. */
export function isPhoto(file: File): boolean {
  return /^image\/(jpeg|png|webp|gif|heic|heif)$/i.test(file.type) || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(file.name);
}

function isText(file: File): boolean {
  return /^text\/(plain|markdown)$/i.test(file.type) || /\.(txt|md|markdown)$/i.test(file.name);
}

/**
 * The name the page will address a photo by, unique among `used` (lower-cased, and added to).
 *
 * The phone's rule (`namesFor` in src/onboarding/pickPhotos.ts): the file's own name without its
 * extension, anything but letters, digits, dot, dash and underscore made a dash, `.jpg` because
 * the file now is one, and `-2`, `-3`… on a clash — with the photos the app ALREADY has too,
 * since a second `beach.jpg` would replace the first on the page. Kept to what the server
 * accepts: /^[\w.-]+$/, at most 120 characters.
 */
export function photoName(fileName: string, used: Set<string>): string {
  const base = fileName.replace(/\.[^.]*$/, '').replace(/[^\w.-]+/g, '-').replace(/^[-.]+|[-.]+$/g, '').slice(0, 100)
    || 'photo';
  let name = `${base}.jpg`;
  for (let n = 2; used.has(name.toLowerCase()); n += 1) name = `${base}-${n}.jpg`;
  used.add(name.toLowerCase());
  return name.slice(0, MAX_NAME);
}

/**
 * Read what was picked, dropped or pasted, against what is already waiting in the composer and
 * the photos the app already has. What cannot be used is said, one sentence each, and never
 * sent: a limit met after the change was paid for is a charge for a refusal.
 *
 * One file at a time, deliberately: ten 12-megapixel photos decoded at once is a phone
 * browser's whole memory.
 */
export async function readPicks(
  files: File[],
  /**
   * `maxPhotos`: the create step's cap (MAX_CREATE_PHOTOS) instead of one chat message's. Given,
   * the sentence about the ones left out says "can be added", not "with one message".
   */
  context: { picked: Picked[]; existing: string[]; maxPhotos?: number },
  deps: PickDeps = {},
): Promise<{ added: Picked[]; problems: string[] }> {
  const toJpeg = deps.toJpeg ?? ((file: File) => browserToJpeg(file));
  const maxPhotos = context.maxPhotos ?? MAX_PHOTOS;
  const added: Picked[] = [];
  const problems: string[] = [];
  const used = new Set([...context.existing, ...context.picked.filter((p) => p.kind === 'photo').map((p) => p.name)]
    .map((n) => n.toLowerCase()));
  let photos = context.picked.filter((p) => p.kind === 'photo').length;
  let texts = context.picked.filter((p) => p.kind === 'text').length;
  let chars = documentsOf(context.picked).reduce((sum, d) => sum + d.text.length, 0);
  let tooMany = 0;
  let overSite = 0;

  for (const file of files) {
    if (isPhoto(file)) {
      if (photos >= maxPhotos) {
        tooMany += 1;
        continue;
      }
      // Every new photo gets a name the site does not have yet (`photoName`), so each one adds.
      if (context.existing.length + photos >= MAX_SITE_PHOTOS) {
        overSite += 1;
        continue;
      }
      let jpeg: Blob;
      try {
        jpeg = await toJpeg(file);
      } catch {
        problems.push(`Could not read ${file.name} — save it as JPEG and add it again.`);
        continue;
      }
      const name = photoName(file.name, used);
      added.push({ kind: 'photo', key: newKey(), name, original: file.name, file: new File([jpeg], name, { type: 'image/jpeg' }) });
      photos += 1;
    } else if (isText(file)) {
      if (texts >= MAX_TEXT_FILES) {
        problems.push(`Up to ${MAX_TEXT_FILES} text files can go with one message, so ${file.name} was left out.`);
        continue;
      }
      let body: string;
      try {
        body = await readText(file);
      } catch {
        problems.push(`Could not read ${file.name}. Try adding it again.`);
        continue;
      }
      /*
       * The server refuses a change with an empty document (editAttachments.ts) — but only after
       * the photos in the same message were uploaded (F22, deep-dive 2026-09-30). Said now.
       */
      if (!body.trim()) {
        problems.push(`${file.name} is empty, so there is nothing in it to add.`);
        continue;
      }
      if (body.length > MAX_TEXT_CHARS) {
        problems.push(
          `${file.name} is too long — it has ${body.length.toLocaleString('en-US')} characters, and a text file can have up to ${MAX_TEXT_CHARS.toLocaleString('en-US')}. Send the part that matters.`,
        );
        continue;
      }
      if (chars + body.length > MAX_TEXT_TOTAL) {
        problems.push(
          `${file.name} was left out — text files sent together can have up to ${MAX_TEXT_TOTAL.toLocaleString('en-US')} characters.`,
        );
        continue;
      }
      added.push({ kind: 'text', key: newKey(), name: file.name.slice(0, MAX_NAME), size: file.size, text: body });
      texts += 1;
      chars += body.length;
    } else {
      problems.push(`${file.name} can’t be added yet — add photos, or a .txt or .md file.`);
    }
  }
  if (tooMany) {
    const where = context.maxPhotos === undefined ? 'can go with one message' : 'can be added';
    problems.push(`Up to ${maxPhotos} photos ${where}, so ${tooMany} ${tooMany === 1 ? 'was' : 'were'} left out.`);
  }
  if (overSite) {
    problems.push(
      `A site can keep up to ${MAX_SITE_PHOTOS} photos, and this one has ${context.existing.length} — so ${overSite} ${overSite === 1 ? 'was' : 'were'} left out.`,
    );
  }
  return { added, problems };
}

/**
 * A text file as UTF-8. `Blob.text()` where the browser has it (it always decodes UTF-8); a
 * FileReader told to, where it does not — an older Safari, and jsdom.
 */
function readText(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
    reader.readAsText(file, 'utf-8');
  });
}

/** The text files, in the shape POST /edit takes them. */
export function documentsOf(picked: Picked[]): { name: string; text: string }[] {
  return picked.flatMap((p) => (p.kind === 'text' ? [{ name: p.name, text: p.text }] : []));
}

/*
 * ─── The line under each photo (owner decision D2, 2026-09-30) ───────────────────────────────
 *
 * The demo's sites looked right because their photos were NAMED vineyard-walk.jpg and
 * storefront.jpg, and the model chose the hero and wrote alt text and captions from the names
 * (deep-dive F9). A real owner's photos are IMG_4821.JPG. So each photo is described once, by the
 * vision model, from a small copy — and the answer goes in a box under the thumbnail that the
 * person can change or clear. The build is told what the box says, not what the model said.
 *
 * Shared: the create step uses it now, and chat can use the same call for photos sent with a
 * change. The contract is POST /describe-photos (C3): up to 12 photos a call, each a JPEG no wider
 * than 768px as base64 of at most 300KB; free to any account, capped per day on the server.
 */

/** The width the vision model is shown: enough to tell a storefront from a latte, and small. */
export const DESCRIBE_WIDTH = 768;
const DESCRIBE_QUALITY = 0.8;
/** The server's cap on one photo's base64. */
export const MAX_DESCRIBE_BASE64 = 300_000;
/** The server's cap on photos per call. */
export const MAX_DESCRIBE_PHOTOS = 12;
/** One plain sentence; the server keeps it this short and so does anything read here. */
export const MAX_DESCRIPTION_CHARS = 140;

export interface PhotoDescription {
  description: string;
  /** The model thinks it would suit the top of the page. Only a real `true` counts. */
  hero: boolean;
}

export interface DescribeDeps {
  /** A JPEG no wider than DESCRIBE_WIDTH. Throws when the file cannot be decoded. */
  toJpeg?: (file: File) => Promise<Blob>;
  /** The call itself — POST /describe-photos. */
  post?: (body: { photos: { name: string; jpegBase64: string }[] }) => Promise<unknown>;
}

/** The small copy the vision call is shown — the same re-encoder, narrower and lighter. */
export function toDescribeJpeg(file: Blob): Promise<Blob> {
  return browserToJpeg(file, DESCRIBE_WIDTH, DESCRIBE_QUALITY);
}

/** A Blob's bytes as plain base64 (no `data:` prefix), through the FileReader every browser has. */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('could not read that photo'));
    reader.onload = () => {
      const url = String(reader.result ?? '');
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });
}

function postDescribe(body: { photos: { name: string; jpegBase64: string }[] }): Promise<unknown> {
  // The build service, beside /plan and /build, where the vision path already runs.
  return request('/describe-photos', { method: 'POST', base: 'build', body, timeoutMs: 60_000 });
}

/**
 * One line for each photo, by name — or none. NEVER throws and never blocks: a refusal (the
 * account's daily cap), a failure or a slow network leaves the boxes empty, and the build goes
 * ahead exactly as it did before descriptions existed. An answer for a name that was not asked
 * about is dropped, so words can never land under the wrong photo.
 */
export async function describePhotos(
  photos: { name: string; file: File }[],
  deps: DescribeDeps = {},
): Promise<Record<string, PhotoDescription>> {
  const toJpeg = deps.toJpeg ?? toDescribeJpeg;
  const post = deps.post ?? postDescribe;
  const out: Record<string, PhotoDescription> = {};

  // One at a time, for readPicks' reason: a dozen photos decoded at once is a phone's memory.
  const ready: { name: string; jpegBase64: string }[] = [];
  for (const photo of photos) {
    try {
      const jpegBase64 = await blobToBase64(await toJpeg(photo.file));
      if (jpegBase64 && jpegBase64.length <= MAX_DESCRIBE_BASE64) ready.push({ name: photo.name, jpegBase64 });
    } catch {
      /* this one stays undescribed; its box is simply empty */
    }
  }

  for (let i = 0; i < ready.length; i += MAX_DESCRIBE_PHOTOS) {
    const batch = ready.slice(i, i + MAX_DESCRIBE_PHOTOS);
    const asked = new Set(batch.map((p) => p.name));
    let answer: unknown;
    try {
      answer = await post({ photos: batch });
    } catch {
      continue;
    }
    const rows = (answer as { photos?: unknown } | null)?.photos;
    if (!Array.isArray(rows)) continue;
    for (const row of rows as { name?: unknown; description?: unknown; hero?: unknown }[]) {
      if (typeof row?.name !== 'string' || !asked.has(row.name) || typeof row.description !== 'string') continue;
      const description = row.description.replace(/\s+/g, ' ').trim().slice(0, MAX_DESCRIPTION_CHARS).trim();
      if (!description) continue;
      out[row.name] = { description, hero: row.hero === true };
    }
  }
  return out;
}

/**
 * The browser's own decoder, then a canvas. `createImageBitmap` first — it applies the photo's
 * EXIF orientation — and an <img> when it refuses, since Safari decodes HEIC that way.
 */
async function browserToJpeg(file: Blob, maxWidth = MAX_PHOTO_WIDTH, quality = JPEG_QUALITY): Promise<Blob> {
  const image = await decode(file);
  try {
    const scale = image.width > maxWidth ? maxWidth / image.width : 1;
    const width = Math.max(1, Math.round(image.width * scale));
    const height = Math.max(1, Math.round(image.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser cannot draw images.');
    // A JPEG has no transparency: a PNG's clear parts would come out black. White behind them.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image.source, 0, 0, width, height);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The photo could not be encoded.'))), 'image/jpeg', quality);
    });
  } finally {
    image.release();
  }
}

async function decode(file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; release: () => void }> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
    } catch {
      /* try the <img> decoder below */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    if (!img.naturalWidth) throw new Error('The photo could not be decoded.');
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, release: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}
